import { useState, useEffect, useRef } from "react";
import Philosophy from "./sections/Philosophy";
import ColorPalette from "./sections/ColorPalette";
import Typography from "./sections/Typography";
import PlatformAndSkill from "./sections/PlatformAndSkill";
import Skill from "./sections/Skill";
import Components from "./sections/Components";
import Gradients from "./sections/Gradients";
import Iconography from "./sections/Iconography";
import MiniPage from "./sections/MiniPage";

const sections = [
  { id: "philosophy", label: "01 — Philosophy" },
  { id: "colour", label: "02 — Colour" },
  { id: "typography", label: "03 — Typography" },
  { id: "platform-skill", label: "04 — Platform" },
  { id: "skill", label: "05 — Skill" },
  { id: "components", label: "06 — Components" },
  { id: "gradients", label: "07 — Gradients" },
  { id: "iconography", label: "08 — Iconography" },
];

function SideNav({ active }) {
  return (
    <nav className="fixed left-0 top-0 z-40 hidden h-screen w-52 border-r border-kin-olive/8 bg-transparent backdrop-blur-sm xl:flex xl:flex-col">
      {/* Brand mark */}
      <div className="px-6 pt-8 pb-6">
        <p className="text-xs font-medium tracking-widest uppercase text-kin-muted">
          Brand Guidelines
        </p>
        <p className="mt-1 text-2xl font-bold tracking-tight text-kin-black font-serif">Kin</p>
      </div>

      {/* Section links */}
      <div className="flex-1 px-3">
        {sections.map((s) => (
          <a
            key={s.id}
            href={`#${s.id}`}
            className={`flex items-center gap-3 rounded-lg px-3 py-3 text-xs font-medium tracking-wide transition ${
              active === s.id
                ? "bg-kin-white/70 text-kin-dark"
                : "text-kin-muted hover:text-kin-body hover:bg-white/50"
            }`}
          >
            {s.label}
          </a>
        ))}
      </div>

      {/* Page links */}
      <div className="px-3 mt-4 border-t border-kin-olive/8 pt-4">
        <p className="px-3 mb-2 text-[10px] font-semibold tracking-widest uppercase text-kin-muted/60">
          Pages
        </p>
        {[
          { href: "/private/design/webapp", label: "Webapp" },
          { href: "/private/design/mobile", label: "Mobile" },
          { href: "/private/design/flows", label: "Core Flows" },
          { href: "/private/design/logo", label: "Logo" },
          { href: "/private/design/landing", label: "Landing Page" },
          { href: "/private/design/marketplace", label: "Marketplace" },
          { href: "/private/design/screen-map", label: "Screen Map" },
          { href: "/private/design/demos", label: "Wireframe Demos" },
        ].map((link) => (
          <a
            key={link.href}
            href={link.href}
            className="flex items-center gap-3 rounded-lg px-3 py-2 text-xs font-medium tracking-wide text-kin-muted transition hover:text-kin-body hover:bg-white/50"
          >
            {link.label}
          </a>
        ))}
      </div>

      {/* Footer */}
      <div className="px-6 pb-6">
        <p className="text-[10px] text-kin-muted/60">Confidential</p>
      </div>
    </nav>
  );
}

function MobileNav({ active }) {
  return (
    <nav className="sticky top-0 z-40 flex items-center gap-1 overflow-x-auto border-b border-kin-olive/10 bg-transparent px-4 py-2 backdrop-blur-sm xl:hidden">
      <span className="mr-2 shrink-0 text-lg font-bold tracking-tight text-kin-black font-serif">
        Kin
      </span>
      {sections.map((s) => (
        <a
          key={s.id}
          href={`#${s.id}`}
          className={`shrink-0 rounded-lg px-3 py-1.5 text-xs font-medium tracking-wide transition ${
            active === s.id ? "bg-kin-white/70 text-kin-dark" : "text-kin-muted hover:text-kin-body"
          }`}
        >
          {s.label}
        </a>
      ))}
      <span className="shrink-0 text-kin-olive/20">|</span>
      <a
        href="/private/design/webapp"
        className="shrink-0 rounded-lg px-3 py-1.5 text-xs font-medium tracking-wide text-kin-muted transition hover:text-kin-body"
      >
        Webapp
      </a>
      <a
        href="/private/design/mobile"
        className="shrink-0 rounded-lg px-3 py-1.5 text-xs font-medium tracking-wide text-kin-muted transition hover:text-kin-body"
      >
        Mobile
      </a>
      <a
        href="/private/design/flows"
        className="shrink-0 rounded-lg px-3 py-1.5 text-xs font-medium tracking-wide text-kin-muted transition hover:text-kin-body"
      >
        Flows
      </a>
      <a
        href="/private/design/demos"
        className="shrink-0 rounded-lg px-3 py-1.5 text-xs font-medium tracking-wide text-kin-muted transition hover:text-kin-body"
      >
        Demos
      </a>
    </nav>
  );
}

function Header() {
  return (
    <header id="top" className="relative overflow-hidden px-8 pb-20 pt-16 xl:pt-20">
      <div className="pointer-events-none absolute inset-0 -z-10 bg-kin-sunrise animate-drift opacity-90" />
      <div className="mx-auto max-w-5xl">
        {/* Label */}
        <p
          className="text-sm font-medium tracking-widest uppercase text-kin-muted animate-fade-up"
          style={{ animationDelay: "0.1s" }}
        >
          Brand Guidelines
        </p>

        {/* Title */}
        <h1
          className="mt-4 text-7xl font-bold tracking-tight text-kin-black font-serif md:text-8xl animate-fade-up"
          style={{ animationDelay: "0.2s" }}
        >
          Kin
        </h1>

        {/* Tagline */}
        <p
          className="mt-4 text-xl text-kin-body font-serif italic animate-fade-up"
          style={{ animationDelay: "0.35s" }}
        >
          Their skills, your health.
        </p>

        {/* Meta */}
        <div
          className="mt-10 flex flex-wrap gap-6 animate-fade-up"
          style={{ animationDelay: "0.5s" }}
        >
          {[
            { label: "Version", value: "1.0" },
            { label: "Updated", value: "February 2026" },
            { label: "Status", value: "Active" },
          ].map(({ label, value }) => (
            <div key={label}>
              <p className="text-[10px] font-semibold tracking-widest uppercase text-kin-muted">
                {label}
              </p>
              <p className="mt-0.5 text-sm font-medium text-kin-body">{value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Decorative element */}
      <div className="pointer-events-none absolute -right-20 -top-20 h-80 w-80 rounded-full bg-kin-green/[0.04] blur-3xl" />
      <div className="pointer-events-none absolute -right-10 top-10 h-48 w-48 rounded-full bg-kin-gold/[0.04] blur-2xl" />
    </header>
  );
}

function useActiveSection() {
  const [active, setActive] = useState("philosophy");

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActive(entry.target.id);
          }
        }
      },
      { rootMargin: "-20% 0px -60% 0px" },
    );

    for (const s of sections) {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    }

    return () => observer.disconnect();
  }, []);

  return active;
}

function useFadeIn() {
  const ref = useRef(null);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-fade-up");
            observer.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.1 },
    );

    if (ref.current) {
      const children = ref.current.querySelectorAll("[data-animate]");
      children.forEach((child) => {
        observer.observe(child);
      });
    }

    return () => observer.disconnect();
  }, []);

  return ref;
}

function App() {
  const active = useActiveSection();
  const mainRef = useFadeIn();

  return (
    <div className="min-h-screen bg-kin-sunrise animate-drift">
      <SideNav active={active} />
      <MobileNav active={active} />

      <div className="xl:ml-52">
        <Header />

        {/* Divider */}
        <div className="mx-8 border-t border-kin-olive/10 gradient-wash animate-drift rounded-full h-px" />

        <main ref={mainRef}>
          <Philosophy />
          <ColorPalette />
          <Typography />
          <PlatformAndSkill />
          <Skill />
          <Components />
          <Gradients />
          <Iconography />
          <MiniPage />
        </main>

        {/* Footer */}
        <footer className="border-t border-kin-olive/10 px-8 py-10 bg-transparent">
          <div className="mx-auto max-w-5xl flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-lg font-serif font-bold text-kin-black">Kin</p>
              <p className="mt-0.5 text-xs text-kin-muted">Brand Book — Confidential</p>
            </div>
            <div className="flex gap-6">
              {sections.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="text-xs text-kin-muted transition hover:text-kin-green"
                >
                  {s.label}
                </a>
              ))}
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default App;
